/**
 * Brigands.
 *
 * The opening of a campaign had exactly one verb in it: attack a nation. That is
 * a bad first hour — every nation on the board starts at war with every other,
 * so the only thing a new player can do with their first host is pick a fight
 * with somebody who can fight back, and the only thing a *cautious* new player
 * can do is nothing at all for twenty turns while the treasury fills.
 *
 * A bandit camp is the other thing to do. It sits on a province and takes a cut
 * of it, so ignoring it is a running cost rather than a missed opportunity; it
 * is worth clearing at a strength a first host can actually bring; and it pays
 * out when it is cleared, so the first campaign a player fights funds the second.
 *
 * Three rules keep them from being either a nuisance or a ninth power:
 *
 *   **They are hostile to everyone, including each other's owners.** A camp on
 *   Karrun-Dûr's ground chokes Karrun-Dûr exactly as hard as one on yours. This
 *   is also, quietly, the map's own answer to a runaway empire: conquest breeds
 *   unrest, unrest breeds camps, and a nation that has swallowed six provinces
 *   in ten turns is now garrisoning six unhappy ones with brigands in the hills.
 *
 *   **They spread if ignored.** An old camp puts a second camp next door. A map
 *   whose neutral threat can be safely ignored goes quiet, and a quiet map is
 *   the failure mode this whole system exists to prevent.
 *
 *   **They fight through the existing battle path.** A camp is a defender with a
 *   strength number, which is precisely what `PendingBattle` already carries.
 *   There is no second combat model here and there must never be one.
 *
 * Every roll in this file comes from `w.rngCampaign`.
 */

import { nextFloat, nextInt, type Rng } from '../../core/rng.ts';
import { nationCapital, NATIONS } from '../../data/nations.ts';
import { provinceDef } from '../../data/provinces.ts';
import { neighbours } from './graph.ts';
import { armiesStandingIn } from './armies.ts';
import { logLine, provinceName } from './state.ts';
import {
  BOUNTY_DAMAGE,
  BOUNTY_TURNS,
  CAMP_GROWTH,
  CAMP_MAX_STRENGTH,
  CAMP_RAID_GARRISON,
  CAMP_SEED_CHANCE,
  CAMP_SPREAD_CHANCE,
  CAMP_SPREAD_TURNS,
  CAMP_START_STRENGTH,
  CAMP_YIELD_CHOKE,
  MAX_CAMPS,
  START_CAMPS,
  type BanditCamp,
  type CampaignState,
} from './types.ts';

export function campAt(c: CampaignState, province: number): BanditCamp | undefined {
  return c.camps.find((k) => k.at === province);
}

export function hasCamp(c: CampaignState, province: number): boolean {
  return c.camps.some((k) => k.at === province);
}

/** The multiplier a camp puts on everything its province yields. */
export function campYieldScale(c: CampaignState, province: number): number {
  return hasCamp(c, province) ? CAMP_YIELD_CHOKE : 1;
}

export function removeCamp(c: CampaignState, province: number): void {
  const i = c.camps.findIndex((k) => k.at === province);
  if (i >= 0) c.camps.splice(i, 1);
}

/** Camps are kept in province order so iteration, hashing and saving are stable. */
function addCamp(c: CampaignState, province: number, strength: number): BanditCamp {
  const camp: BanditCamp = {
    at: province,
    strength: Math.round(strength),
    founded: c.turn,
    bounty: 0,
  };
  c.camps.push(camp);
  c.camps.sort((a, b) => a.at - b.at);
  return camp;
}

/**
 * Ground brigands will settle on.
 *
 * Not a capital, not a province with a host standing on it, not one already
 * under siege, and not one that already has a camp. Everything else is fair —
 * a camp on the player's own bread country is the interesting case, not an
 * unfair one, and it is the case the bounty exists to answer.
 */
function habitable(c: CampaignState, p: number): boolean {
  if (p < 0 || p >= c.provinces.length) return false;
  if (hasCamp(c, p)) return false;
  if (CAPITALS.has(p) || p === c.capital) return false;
  const prov = c.provinces[p];
  if (prov.siegeBy >= 0) return false;
  if (armiesStandingIn(c, p).length > 0) return false;
  return true;
}

const CAPITALS = new Set<number>(NATIONS.map((_, i) => nationCapital(i)));

/**
 * How inviting a province looks to brigands: thin garrisons, angry people, and
 * ground nobody has claimed. Wealth counts too, because there has to be
 * something worth stealing.
 */
function appeal(c: CampaignState, p: number): number {
  const prov = c.provinces[p];
  const unowned = prov.owner < 0 ? 2 : 1;
  const thin = 1 + Math.max(0, 120 - prov.garrison) / 120;
  const angry = 1 + prov.unrest * 2.5;
  const rich = 0.5 + provinceDef(p).wealth / 20;
  return unowned * thin * angry * rich;
}

/** Weighted pick over a candidate list. Deterministic given the stream. */
function pickWeighted(rng: Rng, candidates: number[], weights: number[]): number {
  let total = 0;
  for (const wgt of weights) total += wgt;
  if (total <= 0) return candidates.length > 0 ? candidates[nextInt(rng, 0, candidates.length - 1)] : -1;
  let roll = nextFloat(rng) * total;
  for (let i = 0; i < candidates.length; i++) {
    roll -= weights[i];
    if (roll <= 0) return candidates[i];
  }
  return candidates[candidates.length - 1];
}

/**
 * Put the opening camps on the board.
 *
 * Deterministic from the campaign RNG rather than fixed in the province table,
 * so two campaigns on the same map do not open identically — but seeded, so the
 * same seed does. Called once, from `initCampaignWorld`, because
 * `createCampaign` draws no random numbers and that property is worth keeping.
 */
export function seedCamps(c: CampaignState, rng: Rng): void {
  for (let n = 0; n < START_CAMPS; n++) {
    const candidates: number[] = [];
    const weights: number[] = [];
    for (let p = 0; p < c.provinces.length; p++) {
      if (!habitable(c, p)) continue;
      candidates.push(p);
      weights.push(appeal(c, p));
    }
    if (candidates.length === 0) return;
    const at = pickWeighted(rng, candidates, weights);
    if (at < 0) return;
    addCamp(c, at, CAMP_START_STRENGTH);
  }
  for (const camp of c.camps) {
    logLine(c, `Brigands hold ${provinceName(camp.at)}, and hold it against everyone.`);
  }
}

/**
 * A turn of banditry: grow, raid, answer bounties, and spread.
 *
 * Runs in the income phase, after provinces have paid, so a camp founded this
 * turn does not retroactively tax a province it was not on.
 */
export function stepBandits(c: CampaignState, rng: Rng): void {
  // Grow and raid.
  for (const camp of c.camps) {
    camp.strength = Math.min(CAMP_MAX_STRENGTH, camp.strength + CAMP_GROWTH);
    const prov = c.provinces[camp.at];
    if (prov.owner >= 0 && prov.siegeBy < 0) {
      prov.garrison = Math.max(0, prov.garrison - CAMP_RAID_GARRISON);
      // Brigands on your ground are an argument that you do not hold it.
      prov.unrest = Math.min(1, prov.unrest + 0.03);
    }
  }

  // Bounties. Paid pressure, applied before spreading so a camp that dies this
  // turn cannot found a child on its way out.
  for (const camp of c.camps.slice()) {
    if (camp.bounty <= 0) continue;
    camp.bounty--;
    camp.strength -= BOUNTY_DAMAGE;
    if (camp.strength <= 0) {
      logLine(
        c,
        `The bounty on ${provinceName(camp.at)} is claimed. What is left of the camp scatters.`,
      );
      removeCamp(c, camp.at);
    }
  }

  spreadCamps(c, rng);
}

/**
 * Spreading, in two ways, both of them rolls the player can reason about.
 *
 * An *old* camp — one that has stood for a dozen turns without anybody coming
 * for it — puts a camp on a neighbouring province. And unrest anywhere on the
 * board can breed one from nothing, which is what turns a fast conquest into a
 * policing problem.
 */
function spreadCamps(c: CampaignState, rng: Rng): void {
  if (c.camps.length >= MAX_CAMPS) return;

  const veterans = c.camps.filter((k) => c.turn - k.founded >= CAMP_SPREAD_TURNS);
  for (const camp of veterans) {
    if (c.camps.length >= MAX_CAMPS) return;
    if (nextFloat(rng) > CAMP_SPREAD_CHANCE) continue;
    const candidates: number[] = [];
    const weights: number[] = [];
    for (const p of neighbours(c, camp.at)) {
      if (!habitable(c, p)) continue;
      candidates.push(p);
      weights.push(appeal(c, p));
    }
    if (candidates.length === 0) continue;
    const at = pickWeighted(rng, candidates, weights);
    if (at < 0) continue;
    addCamp(c, at, CAMP_START_STRENGTH);
    logLine(
      c,
      `The camp at ${provinceName(camp.at)} has been left alone long enough to have children. ` +
        `${provinceName(at)} is theirs now.`,
    );
    // Founding a camp resets the parent's clock; one camp does not seed the
    // whole realm in three turns.
    camp.founded = c.turn;
  }

  if (c.camps.length >= MAX_CAMPS) return;
  if (nextFloat(rng) > CAMP_SEED_CHANCE) return;
  const candidates: number[] = [];
  const weights: number[] = [];
  for (let p = 0; p < c.provinces.length; p++) {
    if (!habitable(c, p)) continue;
    // Only genuinely unhappy or genuinely empty ground breeds a camp from
    // nothing. Otherwise camps appear on quiet, well-held provinces, which
    // reads as the map being spiteful rather than as consequences.
    const prov = c.provinces[p];
    if (prov.owner >= 0 && prov.unrest < 0.3) continue;
    candidates.push(p);
    weights.push(appeal(c, p));
  }
  if (candidates.length === 0) return;
  const at = pickWeighted(rng, candidates, weights);
  if (at < 0) return;
  addCamp(c, at, CAMP_START_STRENGTH);
  logLine(c, `Somebody has stopped paying anybody. ${provinceName(at)} has brigands.`);
}

/** Post a bounty. Returns false and spends nothing when it cannot be posted. */
export function tryPostBounty(
  c: CampaignState,
  nation: number,
  province: number,
  gold: number,
): boolean {
  const camp = campAt(c, province);
  if (!camp) return false;
  if (camp.bounty > 0) return false;
  // You may only pay for order on ground you actually hold. Paying men to fight
  // brigands in somebody else's province is not a bounty, it is a war.
  if (c.provinces[province].owner !== nation) return false;
  const n = c.nations[nation];
  if (!n || !n.alive || n.treasury.gold < gold) return false;
  n.treasury.gold -= gold;
  camp.bounty = BOUNTY_TURNS;
  return true;
}

/** What clearing this camp is worth, for the panel and for the payout. */
export function campLoot(camp: BanditCamp, goldPer: number, ironPer: number): {
  gold: number;
  iron: number;
} {
  return {
    gold: Math.round(camp.strength * goldPer),
    iron: Math.round(camp.strength * ironPer),
  };
}
